import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, Zap } from 'lucide-react';
import type { GeneralSettingsDto, PrinterDto, PrinterTestResult } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';
import { Field, PageHeading, SaveBar, Section, ghostPillClass, inputClass } from './controls';
import { PrintLogoField } from './PrintLogoField';
import { PrinterDialog } from './PrinterDialog';
import {
  printerAddress,
  printerChoiceLabel,
  printerTestPageLines,
  printerTestPageToRaster,
  rememberedVerdict,
  testIsSettled,
  testVerdict,
  TEST_POLL_MS,
  type PrinterTestStatus,
  type TestVerdict,
} from './printerTest';
import { toFieldErrors, useResource } from './useSettings';
import { useGeneralForm, type GeneralSettingsPatch } from './useGeneralForm';
import { useUnsavedGuard } from './useUnsavedGuard';

/** The fields THIS page writes — Confirm and the guard share it. */
function printingPayload(row: GeneralSettingsDto): GeneralSettingsPatch {
  return {
    defaultPrinterId: row.defaultPrinterId,
    copyPrintingCount: row.copyPrintingCount,
    printingPhone: row.printingPhone,
    printingAddress: row.printingAddress,
  };
}

/**
 * Printing — the receipt printers and what a receipt says (SPEC §6.1).
 *
 * TWO KINDS OF SAVE LIVE HERE, and the split is deliberate rather than untidy.
 * Printers are their own records under `/settings/printers` and are created,
 * edited, tested and deleted IMMEDIATELY; the fields below them are columns of
 * the venue row and wait for Confirm. Pressing Confirm therefore never adds a
 * printer, and adding a printer never needs Confirm.
 */
export function PrintingPage() {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const { data, form, set, loading, error, errorFor, setErrors, saving, saved, save } =
    useGeneralForm();

  const printers = useResource<PrinterDto[]>('/settings/printers');
  const [addingPrinter, setAddingPrinter] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<PrinterDto | null>(null);
  const [verdicts, setVerdicts] = useState<Record<string, TestVerdict>>({});

  // A test outlives nothing: leaving the page stops following it. The
  // printer row still gets the bridge's answer, and shows it on return.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The press each printer's line belongs to. Test is pressable again once a
  // test has failed — "wrong IP, fix it, Test again" is the whole point — and
  // the failed test is still being followed then, because an older bridge may
  // yet retry it. Only the newest press may write the line, or the first test's
  // follow-up would paint "failed" over the second one's "printed" until the
  // deadline, and that stale word would stay.
  const latestPress = useRef(new Map<string, number>());
  const pressCount = useRef(0);

  // Only the receipt fields can be unsaved here: printers and the receipt logo
  // save themselves the moment they change.
  useUnsavedGuard(
    !!form &&
      !!data &&
      JSON.stringify(printingPayload(form)) !== JSON.stringify(printingPayload(data)),
  );

  if (loading && !form)
    return <p className="p-6 text-sm text-content-muted">{t('common.loading')}</p>;
  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (!form) return null;

  const venueName = form.venueName;
  const defaultPrinter = (printers.data ?? []).find((p) => p.id === form.defaultPrinterId) ?? null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) return;

    await save(printingPayload(form), t('settings.general.saveFailed'));
  }

  /**
   * Draw the page, hand it to the server, then follow the job until the
   * bridge answers. The POST returns as soon as the bridge HAS the page; the
   * answer — printed, failed and why — arrives on the job a moment later.
   * A newer press on the same printer ends this one's following, silently.
   */
  async function testPrinter(printer: PrinterDto): Promise<void> {
    const press = ++pressCount.current;
    latestPress.current.set(printer.id, press);
    const current = (): boolean => mounted.current && latestPress.current.get(printer.id) === press;
    const show = (verdict: TestVerdict): void => {
      if (current()) setVerdicts((shown) => ({ ...shown, [printer.id]: verdict }));
    };

    show({ state: 'sending' });

    // Null when this browser cannot draw: the typed page prints instead.
    const raster = printerTestPageToRaster(
      printerTestPageLines({
        venueName,
        printer,
        requestedBy: employee?.name ?? null,
        at: new Date(),
      }),
    );

    let result: PrinterTestResult;
    try {
      result = await api.post<PrinterTestResult>(`/settings/printers/${printer.id}/test`, {
        raster,
      });
    } catch (caught) {
      show({
        state: 'error',
        message:
          caught instanceof Error
            ? caught.message
            : t('settings.general.printerTest.requestFailed'),
      });
      return;
    }

    if (!result.printJobId) {
      show({ state: 'error', message: result.message });
      return;
    }

    const started = Date.now();
    for (;;) {
      let status: PrinterTestStatus | null = null;
      try {
        status = await api.get<PrinterTestStatus>(
          `/settings/printers/${printer.id}/test/${result.printJobId}`,
        );
      } catch {
        // One lost poll is not a verdict; the deadline still bounds the wait.
      }
      if (!current()) return;
      const elapsed = Date.now() - started;
      show(testVerdict(status, elapsed));
      if (testIsSettled(status, elapsed)) break;
      await new Promise((resolve) => setTimeout(resolve, TEST_POLL_MS));
      if (!current()) return;
    }

    // The printer row now carries the bridge's answer as its last test.
    void printers.reload();
  }

  async function deletePrinter(id: string): Promise<void> {
    setErrors([]);
    try {
      await api.delete(`/settings/printers/${id}`);
      await printers.reload();
    } catch (caught) {
      /*
       * `printer_has_jobs` is not a refusal, it is a question.
       *
       * Nothing routes to the printer; it is only held by tickets queued for a
       * print bridge. The server refuses ONCE, with the count, so this can ask
       * before anything is discarded — and so the old dead end (a printer that
       * had ever queued a job could never be deleted, under a message blaming
       * routing that did not exist) cannot come back.
       */
      if (caught instanceof ApiError && caught.code === 'printer_has_jobs') {
        const count =
          (caught.details as { printJobCount?: number } | undefined)?.printJobCount ?? 0;
        if (!window.confirm(t('settings.general.printerDiscardJobs', { times: count }))) return;

        try {
          await api.delete(`/settings/printers/${id}?discardJobs=true`);
          await printers.reload();
          return;
        } catch (retried) {
          setErrors(toFieldErrors(retried, t('settings.general.printerDeleteFailed')));
          return;
        }
      }

      setErrors(toFieldErrors(caught, t('settings.general.printerDeleteFailed')));
    }
  }

  return (
    <>
      <form onSubmit={(event) => void handleSubmit(event)} className="p-6">
        <PageHeading
          title={t('settings.general.printingTitle')}
          description={t('settings.general.printingDescription')}
        />

        <div className="grid gap-4 lg:grid-cols-2">
          <Section id="set-printers" title={t('settings.general.printersSection')}>
            {/* Printers list with add (+), each editable, testable and deletable. */}
            <div className="space-y-2">
              {(printers.data ?? []).map((printer) => (
                <PrinterRow
                  key={printer.id}
                  printer={printer}
                  verdict={verdicts[printer.id] ?? rememberedVerdict(printer)}
                  remembered={!verdicts[printer.id]}
                  busy={
                    verdicts[printer.id]?.state === 'sending' ||
                    verdicts[printer.id]?.state === 'waiting'
                  }
                  onTest={() => void testPrinter(printer)}
                  onEdit={() => setEditingPrinter(printer)}
                  onDelete={() => void deletePrinter(printer.id)}
                />
              ))}

              {printers.data !== null && printers.data.length === 0 ? (
                <p className="text-sm text-content-muted">{t('settings.general.noPrinters')}</p>
              ) : null}

              <button
                type="button"
                onClick={() => setAddingPrinter(true)}
                className={ghostPillClass}
              >
                <span className="inline-flex items-center gap-1">
                  <Plus className="h-4 w-4" aria-hidden />
                  {t('settings.general.addPrinter')}
                </span>
              </button>

              <p className="text-xs text-content-muted">{t('settings.general.printerTest.hint')}</p>
            </div>

            <Field
              label={t('settings.general.defaultPrinter')}
              hint={
                defaultPrinter && !defaultPrinter.isActive
                  ? t('settings.general.defaultPrinterOff')
                  : undefined
              }
            >
              <select
                value={form.defaultPrinterId ?? ''}
                onChange={(event) => set('defaultPrinterId', event.target.value || null)}
                className={inputClass}
              >
                <option value="">{t('settings.general.noDefaultPrinter')}</option>
                {(printers.data ?? []).map((printer) => (
                  <option key={printer.id} value={printer.id}>
                    {printerChoiceLabel(printer, t('settings.general.printerOff'))}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section id="set-receipt" title={t('settings.general.receiptSection')}>
            <Field label={t('settings.general.copyPrinting')} error={errorFor('copyPrintingCount')}>
              <input
                type="number"
                min="1"
                max="10"
                value={form.copyPrintingCount}
                onChange={(event) => set('copyPrintingCount', Number(event.target.value) || 1)}
                className={`${inputClass} tabular`}
              />
            </Field>

            <Field label={t('settings.general.phoneForPrinting')}>
              <input
                value={form.printingPhone ?? ''}
                onChange={(event) => set('printingPhone', event.target.value || null)}
                className={inputClass}
              />
            </Field>

            <Field label={t('settings.general.printingAddress')}>
              <input
                value={form.printingAddress ?? ''}
                onChange={(event) => set('printingAddress', event.target.value || null)}
                className={inputClass}
              />
            </Field>

            {/* The receipt logo: its own artwork, converted to printer dots in
              the browser when it is chosen. It saves itself — see
              PrintLogoField — which is why it sits with the receipt's other
              content rather than with the venue logo it is not. */}
            <PrintLogoField />
          </Section>
        </div>

        <SaveBar saving={saving} saved={saved} error={errorFor('(root)')} />
      </form>

      {/*
       * OUTSIDE the form above, and it must stay outside. The dialog carries a
       * <form> of its own, and a nested <form> is invalid HTML — React neuters
       * the inner one and its Add button silently dispatches nothing. That is
       * the bug that left the venue unable to add a printer at all. The dialog
       * also portals itself to <body>; this is the second lock on the same door.
       */}
      {addingPrinter ? (
        <PrinterDialog
          onClose={() => setAddingPrinter(false)}
          onCreated={() => void printers.reload()}
        />
      ) : null}
      {editingPrinter ? (
        <PrinterDialog
          printer={editingPrinter}
          onClose={() => setEditingPrinter(null)}
          onCreated={() => void printers.reload()}
        />
      ) : null}
    </>
  );
}

/** One printer: where it is, what its last test said, and its three buttons. */
export function PrinterRow({
  printer,
  verdict,
  remembered,
  busy,
  onTest,
  onEdit,
  onDelete,
}: {
  printer: PrinterDto;
  verdict: TestVerdict | null;
  /** The verdict is the printer row's memory, not a test run on this page. */
  remembered: boolean;
  busy: boolean;
  onTest: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2 rounded-control border border-subtle bg-input px-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm text-content">{printer.name}</span>
          {printer.isActive ? null : (
            <span className="rounded-full border border-subtle px-2 text-xs text-content-muted">
              {t('settings.general.printerOff')}
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-content-muted">{printerAddress(printer)}</span>
        {verdict ? (
          <VerdictLine
            verdict={verdict}
            usb={printer.connection === 'USB'}
            testedAt={remembered ? printer.lastTestAt : null}
          />
        ) : null}
      </span>

      <button
        type="button"
        onClick={onTest}
        disabled={busy}
        title={t('settings.general.testPrinter')}
        aria-label={t('settings.general.testPrinter')}
        className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover disabled:opacity-50"
      >
        <Zap className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onEdit}
        title={t('settings.general.editPrinter')}
        aria-label={t('settings.general.editPrinter')}
        className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover"
      >
        <Pencil className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onDelete}
        title={t('common.delete')}
        aria-label={t('common.delete')}
        className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

/** "HH:MM" of an ISO instant, on this till's clock. */
function clockOf(iso: string | null): string {
  if (!iso) return '';
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ''
    : `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

/** A remembered verdict carries its date, so an old one reads as old. */
function dateOf(iso: string | null): string {
  if (!iso) return '';
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ''
    : `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
}

export function VerdictLine({
  verdict,
  usb,
  testedAt,
}: {
  verdict: TestVerdict;
  usb: boolean;
  /** Set for a remembered verdict: when that test ran. */
  testedAt: string | null;
}) {
  const { t } = useTranslation();

  let text: string;
  let tone: 'muted' | 'success' | 'danger';
  switch (verdict.state) {
    case 'sending':
      text = t('settings.general.printerTest.sending');
      tone = 'muted';
      break;
    case 'waiting':
      text = t('settings.general.printerTest.waiting');
      tone = 'muted';
      break;
    case 'printed':
      text = [
        t(
          usb ? 'settings.general.printerTest.printedUsb' : 'settings.general.printerTest.printed',
          { time: clockOf(verdict.at) },
        ),
        verdict.drawn === false ? t('settings.general.printerTest.typedPage') : '',
      ]
        .filter(Boolean)
        .join(' ');
      tone = 'success';
      break;
    case 'failed':
      text = t('settings.general.printerTest.failed', {
        reason: t(`settings.general.printerTest.reason.${verdict.code}`),
      });
      // The bridge's own words ("BAR: connect ETIMEDOUT 192.168.182.99:9100")
      // are what someone fixing the printer needs; "no bridge" has none.
      if (verdict.detail && verdict.code !== 'no_bridge') text += ` (${verdict.detail})`;
      tone = 'danger';
      break;
    case 'noAnswer':
      text = t('settings.general.printerTest.noAnswer');
      tone = 'danger';
      break;
    case 'error':
      text = verdict.message;
      tone = 'danger';
      break;
  }

  const prefix = testedAt
    ? `${t('settings.general.printerTest.lastTest', { date: dateOf(testedAt) })} `
    : '';

  return (
    <span
      role={verdict.state === 'sending' || verdict.state === 'waiting' ? 'status' : undefined}
      className={`block text-xs ${
        tone === 'success'
          ? 'text-success'
          : tone === 'danger'
            ? 'text-danger'
            : 'text-content-muted'
      }`}
    >
      {prefix}
      {text}
    </span>
  );
}
